import { useState, useEffect, useRef } from "react";
import { saveReferral } from "@/lib/referral";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe, Star, Shield, Zap, Users, ArrowRight, Lock, Sparkles, Heart, Briefcase, Brain, Gem, Target, Sun, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useLanguage } from "@/contexts/LanguageContext";
import { SocialProofToast, CountdownTimer } from "@/components/CROWidgets";
import { GA } from "@/lib/analytics";

const fadeUp = { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [.22,1,.36,1] } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } };
const scaleIn = { hidden: { opacity: 0, scale: 0.9 }, visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: [.22,1,.36,1] } } };

// Falling petals component
function FallingPetals() {
  const petals = Array.from({ length: 12 }, (_, i) => ({
    id: i, left: Math.random() * 100, delay: Math.random() * 8, duration: 6 + Math.random() * 6,
    size: 12 + Math.random() * 14, type: i % 3 === 0 ? "🌹" : i % 3 === 1 ? "✨" : "💸",
  }));
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
      {petals.map(p => (
        <span key={p.id} className="petal absolute text-lg select-none"
          style={{ left: `${p.left}%`, top: '-5%', fontSize: `${p.size}px`, animationDuration: `${p.duration}s`, animationDelay: `${p.delay}s` }}>
          {p.type}
        </span>
      ))}
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language, toggleLanguage, t } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  // Capture referral code from ?ref= silently
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) {
      saveReferral(ref);
      // Track click on backend
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/referral/click?ref_code=${ref}`, { method: "POST" }).catch(() => {});
    }
  }, [searchParams]);

  // GA4 track landing page view
  useEffect(() => { GA.pageView("Landing Page"); }, []);

  const testimonials = [
    { name: "Priya S.", text: t("testimonial1"), avatar: "P" },
    { name: "Rahul M.", text: t("testimonial2"), avatar: "R" },
    { name: "Anita K.", text: t("testimonial3"), avatar: "A" },
  ];

  useEffect(() => {
    const timer = setInterval(() => setCurrentTestimonial(p => (p + 1) % testimonials.length), 4000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  const features = [
    { icon: <Heart className="w-6 h-6" />, title: t("insight1Title"), desc: t("insight1Desc") },
    { icon: <Briefcase className="w-6 h-6" />, title: t("insight2Title"), desc: t("insight2Desc") },
    { icon: <Brain className="w-6 h-6" />, title: t("insight3Title"), desc: t("insight3Desc") },
    { icon: <Target className="w-6 h-6" />, title: t("insight4Title"), desc: t("insight4Desc") },
    { icon: <Gem className="w-6 h-6" />, title: language === "hi" ? "Lucky Numbers" : "Lucky Numbers & Colors", desc: language === "hi" ? "अपने भाग्यशाली नंबर और रंग जानें" : "Discover your lucky numbers, colors and auspicious days" },
    { icon: <Sun className="w-6 h-6" />, title: language === "hi" ? "Daily Guidance" : "Life Path Guidance", desc: language === "hi" ? "जीवन पथ मार्गदर्शन" : "Get direction on your life path and hidden talents" },
  ];

  const reportCards = [
    { emoji: "❤️", label: language === "hi" ? "प्यार का स्कोर" : "Love Score", score: "87%", color: "#FF4D6A" },
    { emoji: "💰", label: language === "hi" ? "धन स्कोर" : "Wealth Score", score: "72%", color: "#FFC107" },
    { emoji: "💼", label: language === "hi" ? "करियर" : "Career Match", score: "91%", color: "#4CAF50" },
    { emoji: "🧠", label: language === "hi" ? "व्यक्तित्व" : "Personality", score: "A+", color: "#2196F3" },
    { emoji: "💍", label: language === "hi" ? "भावी साथी" : "Future Partner", score: "High", color: "#E91E63" },
    { emoji: "🌟", label: language === "hi" ? "छिपी प्रतिभा" : "Hidden Talent", score: "3 Found", color: "#9C27B0" },
  ];

  const scrollToReport = () => document.getElementById("report-preview")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen bg-white relative overflow-x-hidden">
      {/* ===== STICKY NAVBAR ===== */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "bg-white/90 backdrop-blur-xl shadow-sm border-b border-gray-100" : "bg-transparent"}`} data-testid="navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
            <img src="/logo.jpg" alt="Vishu Astro" className="w-9 h-9 rounded-full object-cover shadow-md" />
            <span className="font-bold text-lg tracking-tight text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>Vishu Astro</span>
          </Link>
          <div className="hidden md:flex items-center gap-1">
            {[
              { label: t("navHome") || "Home", href: "#", id: null },
              { label: t("navReading") || "Reading", href: "/reading", id: null },
              { label: language === "hi" ? "मूल्य" : "Pricing", href: "#report-preview", id: "report-preview" },
              { label: t("navFeatures") || "Features", href: "#features", id: "features" },
              { label: t("navFaq") || "FAQ", href: "#faq", id: "faq" },
              { label: t("privacy"), href: "/legal/privacy", id: null },
            ].map((item, i) => (
              <a key={i} href={item.href}
                onClick={(e) => {
                  if (item.href.startsWith("/")) { e.preventDefault(); navigate(item.href); }
                  else if (item.id) { e.preventDefault(); document.getElementById(item.id)?.scrollIntoView({ behavior: "smooth" }); }
                  else if (item.href === "#") { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }
                }}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-50 transition-all">
                {item.label}
              </a>
            ))}
            <button onClick={toggleLanguage} className="px-3 py-1.5 text-xs font-semibold text-gray-500 hover:text-gray-900 rounded-full border border-gray-200 hover:border-gray-300 transition-all ml-2 flex items-center gap-1.5" data-testid="lang-toggle-btn">
              <Globe className="w-3.5 h-3.5" /> {t("langToggle")}
            </button>
            <Button onClick={() => navigate("/reading")} className="btn-primary ml-3 px-6 py-2.5 text-sm" data-testid="nav-cta-btn">
              {t("heroCta")}
            </Button>
          </div>
          <div className="md:hidden flex items-center gap-2">
            <button onClick={toggleLanguage} className="p-2 text-gray-500" data-testid="lang-toggle-btn"><Globe className="w-5 h-5" /></button>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 text-gray-700" data-testid="mobile-menu-btn">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3 }}
              className="md:hidden bg-white/95 backdrop-blur-xl border-b border-gray-100 overflow-hidden">
              <div className="px-6 py-4 space-y-1">
                {[
                  { label: "Home", action: () => window.scrollTo({ top: 0, behavior: "smooth" }) },
                  { label: "Reading", action: () => navigate("/reading") },
                  { label: "Pricing", action: () => document.getElementById("report-preview")?.scrollIntoView({ behavior: "smooth" }) },
                  { label: "Features", action: () => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" }) },
                  { label: "FAQ", action: () => document.getElementById("faq")?.scrollIntoView({ behavior: "smooth" }) },
                  { label: t("privacy"), action: () => navigate("/legal/privacy") },
                ].map((item, i) => (
                  <button key={i}
                    onClick={() => { setMobileMenuOpen(false); setTimeout(() => item.action(), 100); }}
                    className="block w-full text-left px-4 py-3 text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-xl transition-all">{item.label}</button>
                ))}
                <Button onClick={() => { navigate("/reading"); setMobileMenuOpen(false); }} className="btn-primary w-full mt-3 py-3 text-base">
                  {t("heroCta")}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* ===== HERO SECTION ===== */}
      <section className="relative min-h-screen flex items-center justify-center pt-14 overflow-hidden" data-testid="hero-section">
        <FallingPetals />
        <div className="absolute inset-0 bg-gradient-to-b from-amber-50/40 via-white to-white pointer-events-none z-0" />
        <motion.div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 text-center py-8 sm:py-14">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            {/* Logo floating */}
            <motion.div variants={fadeUp} className="flex justify-center mb-5">
              <div className="relative">
                <div className="absolute inset-0 w-20 h-20 sm:w-28 sm:h-28 rounded-full bg-gradient-to-br from-amber-300 to-yellow-500 blur-2xl opacity-50" />
                <img src="/logo.jpg" alt="Vishu Astro" className="w-20 h-20 sm:w-28 sm:h-28 rounded-full object-cover shadow-xl relative z-10 border-4 border-white" />
              </div>
            </motion.div>

            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-50 border border-amber-200 rounded-full text-sm font-medium text-amber-700 mb-4">
              <Sparkles className="w-4 h-4" /> {language === "hi" ? "AI-Powered Astrology" : "AI-Powered Astrology"} <Sparkles className="w-4 h-4" />
            </motion.div>

            <motion.h1 variants={fadeUp} className="text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.15] mb-4">
              <span className="text-gray-900">{t("heroTitle1")} </span>
              <span className="gold-text">{t("heroTitle2")}</span>
            </motion.h1>

            <motion.p variants={fadeUp} className="text-gray-500 text-base sm:text-lg max-w-xl mx-auto mb-6 leading-relaxed">
              {t("heroSubtitle")}
            </motion.p>

            <motion.div variants={fadeUp} className="mb-5">
              <Button onClick={() => navigate("/reading")}
                className="btn-primary px-10 sm:px-14 py-5 sm:py-6 text-lg sm:text-xl w-full sm:w-auto shadow-lg shadow-amber-200/40 rounded-2xl hover:shadow-xl hover:shadow-amber-300/40 hover:scale-[1.02] transition-all"
                data-testid="start-reading-btn">
                <Sparkles className="w-6 h-6 mr-3" /> {t("heroCta")}
              </Button>
              <p className="text-xs text-gray-400 mt-2.5">{language === "hi" ? "सिर्फ 30 सेकंड में शुरू करें" : "Takes less than 30 seconds to start"}</p>
            </motion.div>

            <motion.div variants={fadeUp} className="flex justify-center mb-5">
              <Button onClick={scrollToReport} variant="ghost" className="text-sm text-gray-400 hover:text-gray-600 transition-all" data-testid="sample-report-btn">
                {language === "hi" ? "सैंपल रिपोर्ट देखें" : "View Sample Report"} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </motion.div>

            {/* Trust badges */}
            <motion.div variants={fadeUp} className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-gray-400 text-xs sm:text-sm">
              <span className="flex items-center gap-1.5"><Users className="w-4 h-4" /> {t("trustUsers")}</span>
              <span className="flex items-center gap-1.5"><Star className="w-4 h-4 text-amber-400 fill-amber-400" /> 4.9 Rating</span>
              <span className="flex items-center gap-1.5"><Shield className="w-4 h-4" /> {t("trustSecure")}</span>
              <span className="flex items-center gap-1.5"><Zap className="w-4 h-4" /> {t("trustInstant")}</span>
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 bg-white" data-testid="how-it-works-section">
        <div className="max-w-6xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger} className="text-center mb-16">
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4 text-gray-900">
              {t("howTitle")}
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-base sm:text-lg max-w-lg mx-auto">{t("howSubtitle")}</motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {[
              { num: "01", icon: "💬", title: t("step1Title"), desc: t("step1Desc") },
              { num: "02", icon: "🔮", title: t("step2Title"), desc: t("step2Desc") },
              { num: "03", icon: "📊", title: t("step3Title"), desc: t("step3Desc") },
            ].map((s, i) => (
              <motion.div key={i} variants={fadeUp} className="glass-card rounded-3xl p-8 text-center group hover:scale-[1.02] transition-transform duration-300" data-testid={`step-card-${i+1}`}>
                <div className="text-5xl mb-4">{s.icon}</div>
                <div className="text-xs font-bold text-amber-500 tracking-widest mb-2">STEP {s.num}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{s.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===== FEATURES ===== */}
      <section id="features" className="py-20 sm:py-28 px-4 sm:px-6 bg-gradient-to-b from-white to-amber-50/30" data-testid="insights-section">
        <div className="max-w-6xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4 text-gray-900">{t("insightsTitle")}</motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-base sm:text-lg max-w-lg mx-auto">{t("insightsSubtitle")}</motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <motion.div key={i} variants={scaleIn} className="glass-card rounded-3xl p-6 sm:p-8 group hover:scale-[1.03] transition-all duration-300">
                <div className="w-12 h-12 rounded-2xl bg-amber-50 flex items-center justify-center text-amber-600 mb-4 group-hover:bg-amber-100 transition-colors">{f.icon}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{f.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===== BLURRED REPORT PREVIEW ===== */}
      <section id="report-preview" className="py-20 sm:py-28 px-4 sm:px-6 bg-white" data-testid="report-preview-section">
        <div className="max-w-6xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-12">
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4 text-gray-900">
              {language === "hi" ? "आपकी रिपोर्ट का प्रीव्यू" : "Your Report Preview"}
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-base sm:text-lg max-w-lg mx-auto">
              {language === "hi" ? "पूरी रिपोर्ट अनलॉक करें — सिर्फ ₹49" : "Unlock the full report — just ₹49"}
            </motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-6 max-w-3xl mx-auto">
            {reportCards.map((c, i) => (
              <motion.div key={i} variants={scaleIn} className="relative glass-card rounded-3xl p-5 sm:p-6 text-center group hover:scale-[1.04] transition-all duration-300 overflow-hidden">
                <div className="text-3xl mb-2">{c.emoji}</div>
                <p className="text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">{c.label}</p>
                <div className="blur-card-content">
                  <p className="text-2xl font-bold" style={{ color: c.color }}>{c.score}</p>
                </div>
                <div className="absolute inset-0 bg-white/30 backdrop-blur-[2px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Lock className="w-6 h-6 text-gray-400" />
                </div>
              </motion.div>
            ))}
          </motion.div>
          {/* Pricing Card */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mt-12 max-w-sm mx-auto">
            <div className="glass-card rounded-3xl p-8 text-center border-2 border-amber-200 animate-pulse-glow">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-red-50 text-red-600 rounded-full text-xs font-bold mb-4">
                81% OFF — Limited Time
              </div>
              <div className="flex items-center justify-center gap-3 mb-2">
                <span className="text-gray-400 line-through text-xl">₹199</span>
                <span className="text-4xl font-bold text-gray-900">₹49</span>
              </div>
              <p className="text-gray-500 text-sm mb-4">{language === "hi" ? "पूरी रिपोर्ट + लाइव चैट" : "Full Report + Live Chat Session"}</p>
              <div className="mb-5"><CountdownTimer /></div>
              <Button onClick={() => navigate("/reading")} className="btn-primary w-full py-4 text-base" data-testid="pricing-cta-btn">
                <Lock className="w-4 h-4 mr-2" /> {t("unlockBtn")}
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 bg-gradient-to-b from-amber-50/30 to-white" data-testid="testimonials-section">
        <div className="max-w-4xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-12">
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4 text-gray-900">{t("testimonialsTitle")}</motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <AnimatePresence mode="wait">
              <motion.div key={currentTestimonial} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.5 }}
                className="glass-card rounded-3xl p-8 sm:p-10 text-center max-w-xl mx-auto" data-testid={`testimonial-card-${currentTestimonial+1}`}>
                <div className="flex gap-1 justify-center mb-4">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-5 h-5 text-amber-400 fill-amber-400" />)}
                </div>
                <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-6 italic">"{testimonials[currentTestimonial].text}"</p>
                <div className="flex items-center justify-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-white font-bold text-sm">
                    {testimonials[currentTestimonial].avatar}
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900 text-sm">{testimonials[currentTestimonial].name}</p>
                    <p className="text-xs text-green-600 font-medium flex items-center gap-1"><Shield className="w-3 h-3" /> Verified User</p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
            <div className="flex justify-center gap-2 mt-6">
              {testimonials.map((_, i) => (
                <button key={i} onClick={() => setCurrentTestimonial(i)} className={`carousel-dot ${i === currentTestimonial ? "active" : ""}`} />
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section id="faq" className="py-20 sm:py-28 px-4 sm:px-6 bg-white" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-12">
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4 text-gray-900">{t("faqTitle")}</motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <Accordion type="single" collapsible className="space-y-3">
              {[
                { q: t("faq1Q"), a: t("faq1A") }, { q: t("faq2Q"), a: t("faq2A") },
                { q: t("faq3Q"), a: t("faq3A") }, { q: t("faq4Q"), a: t("faq4A") },
                { q: t("faq5Q"), a: t("faq5A") },
              ].map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="glass-card rounded-2xl border-none px-6 overflow-hidden" data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-gray-900 font-medium text-sm sm:text-base hover:no-underline py-5">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-500 text-sm leading-relaxed pb-5">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* ===== CTA FOOTER ===== */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 bg-gradient-to-b from-white to-amber-50/50 text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="max-w-2xl mx-auto">
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6 text-gray-900">{t("ctaTitle")}</motion.h2>
          <motion.p variants={fadeUp} className="text-gray-500 text-base sm:text-lg mb-8">{t("ctaSubtitle")}</motion.p>
          <motion.div variants={fadeUp}>
            <Button onClick={() => navigate("/reading")} className="btn-primary px-10 py-4 text-lg" data-testid="footer-cta-btn">
              {t("ctaBtn")} <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </motion.div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="border-t border-gray-100 py-10 px-4 sm:px-6 bg-white" data-testid="footer">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6 mb-6">
            <div className="flex items-center gap-2.5">
              <img src="/logo.jpg" alt="Vishu Astro" className="w-8 h-8 rounded-full object-cover" />
              <span className="font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>Vishu Astro</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
              {[
                { label: t("privacy"), path: "/legal/privacy" }, { label: t("terms"), path: "/legal/terms" },
                { label: t("refund"), path: "/legal/refund" }, { label: t("disclaimer"), path: "/legal/disclaimer" },
                { label: t("contact"), path: "/legal/contact" },
                { label: "Affiliate Program", path: "/affiliate" },
              ].map((l, i) => (
                <Link key={i} to={l.path} className="text-gray-400 hover:text-gray-700 text-xs font-medium transition-colors" data-testid={`footer-link-${i}`}>{l.label}</Link>
              ))}
            </div>
          </div>
          <p className="text-gray-400 text-xs text-center">{t("footerDisclaimer")}</p>
        </div>
      </footer>

      {/* ===== SOCIAL PROOF TOAST ===== */}
      <SocialProofToast language={language} />

      {/* ===== WHATSAPP FLOAT ===== */}
      <a href={`https://api.whatsapp.com/send?text=${encodeURIComponent(language === "hi" ? "Apna future jaano - Free mein prediction lo aur live astrologer se baat karo! 🔮✨ https://vishuastro.in" : "Know your future - Get free prediction & talk to a live astrologer! 🔮✨ https://vishuastro.in")}`}
        target="_blank" rel="noopener noreferrer" data-testid="whatsapp-share-btn"
        className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-40 bg-[#25D366] hover:bg-[#1da851] text-white rounded-full w-12 h-12 md:w-14 md:h-14 flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:scale-110">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
      </a>

    </div>
  );
}
